/* eslint-disable react/no-danger */
import React, { ReactElement, useContext } from 'react';
import { KeyFacts as KeyFactsDTO } from '@oneaudi/avp-hck-dga-customerui-commons';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import styled from 'styled-components';
import { screen, size } from '../../common/breakpoints';
import { PetrolColors } from '../../common/colors';
import { HorizontalSpacing, Spacing, VerticalSpacing } from '../../common/spacings';
import { GiveawayContext } from '../../services/GiveawayContextService';
import { ReactComponent as Fuel } from '../../assets/keyfacts/Fuel.svg';
import { ReactComponent as Paint } from '../../assets/keyfacts/Paint.svg';
import { ReactComponent as Drive } from '../../assets/keyfacts/Adaptative_chasis.svg';
import { ReactComponent as Gear } from '../../assets/keyfacts/Gear_knob.svg';
import { ReactComponent as Power } from '../../assets/keyfacts/Engine.svg';
import { ReactComponent as Rim } from '../../assets/keyfacts/Tyre_insurance.svg';
import { ReactComponent as Interior } from '../../assets/keyfacts/Comfort.svg';
import { ReactComponent as Electric } from '../../assets/keyfacts/Electric_battery.svg';
import { ReactComponent as Range } from '../../assets/keyfacts/Range.svg';

const StyledContainer = styled.div`
  padding: ${VerticalSpacing.S} 0;

  @media screen and ${screen.m} {
    padding: ${VerticalSpacing.M} 0;
  }

  @media screen and ${screen.l} {
    padding: ${VerticalSpacing.L} 0;
  }

  @media screen and ${screen.xl} {
    padding: ${VerticalSpacing.XL} 0;
  }
`;

const StyledKeyFactsContainer = styled.div`
  display: grid;
  justify-content: center;
  flex-direction: column;
  grid-template-columns: repeat(1, 1fr);
  grid-row-gap: 0;
  padding-right: ${HorizontalSpacing.S};
  padding-left: ${HorizontalSpacing.S};

  @media screen and ${screen.s} {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-row-gap: ${HorizontalSpacing.M};
    grid-column-gap: ${HorizontalSpacing.S};
    padding-right: ${HorizontalSpacing.L};
    padding-left: ${HorizontalSpacing.L};
  }

  @media screen and ${screen.m} {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-row-gap: ${HorizontalSpacing.L};
    grid-column-gap: ${HorizontalSpacing.M};
    padding-right: ${HorizontalSpacing.XL};
    padding-left: ${HorizontalSpacing.XL};
  }
`;

const StyledKeyFact = styled.div`
  display: flex;
  width: auto;
  text-align: left;
  justify-content: left;
  align-items: center;
  padding: ${HorizontalSpacing.S};

  @media screen and ${screen.m} {
    flex-direction: column;
    text-align: center;
    padding: 0;
  }

  svg {
    height: 48px;
    width: 48px;
    overflow: visible;
    padding-right: ${Spacing.M};

    @media screen and ${screen.m} {
      padding-bottom: 16px;
      padding-right: 0;
    }
  }

  p {
    font-weight: bold;
    color: ${PetrolColors.Petrol100};
  }
`;

const StyledDiv = styled.div`
  @media screen and (max-width: ${size.m}) {
    display: block;
  }
`;

export type KeyFact = {
  id: number;
  icon: ReactElement;
  title: string;
  data?: string;
  visible: boolean;
};

export const keyFactsDataGrid = (t: TFunction, keyFacts: KeyFactsDTO): KeyFact[] => [
  {
    id: 1,
    icon: <Fuel id="fuelType" />,
    title: t('body.details.keyFacts.fuelType'),
    data: keyFacts.fuelType,
    visible: !keyFacts.range && !!keyFacts.fuelType,
  }, // if electric show "charging"
  {
    id: 2,
    icon: <Electric id="charging" />,
    title: t('body.details.keyFacts.fuelType'),
    data: keyFacts.fuelType,
    visible: !!keyFacts.range && !!keyFacts.fuelType,
  },
  {
    id: 3,
    icon: <Power id="power" />,
    title: t('body.details.keyFacts.power'),
    data: keyFacts.power,
    visible: !!keyFacts.power,
  },
  {
    id: 4,
    icon: <Gear id="transmission" />,
    title: t('body.details.keyFacts.gear'),
    data: keyFacts.gear,
    visible: !keyFacts.range && !!keyFacts.gear,
  },
  {
    id: 5,
    icon: <Range id="range" />,
    title: t('body.details.keyFacts.range'),
    data: keyFacts.range,
    visible: !!keyFacts.range,
  },
  {
    id: 6,
    icon: <Drive id="drive" />,
    title: t('body.details.keyFacts.drive'),
    data: keyFacts.drive,
    visible: !!keyFacts.drive,
  },
  {
    id: 7,
    icon: <Paint id="exteriorColor" />,
    title: t('body.details.keyFacts.exteriorColor'),
    data: keyFacts.exteriorColor,
    visible: !!keyFacts.exteriorColor,
  },
  {
    id: 8,
    icon: <Interior id="interiorColor" />,
    title: t('body.details.keyFacts.interiorColor'),
    data: keyFacts.interiorColor,
    visible: !!keyFacts.interiorColor,
  },
  {
    id: 9,
    icon: <Interior id="interior" />,
    title: t('body.details.keyFacts.interior'),
    data: keyFacts.upholstery,
    visible: !!keyFacts.upholstery,
  },
  {
    id: 10,
    icon: <Rim id="charging" />,
    title: t('body.details.keyFacts.rim'),
    data: keyFacts.rimType,
    visible: !!keyFacts.rimType,
  },
];

export const KeyFacts = (): ReactElement => {
  const { t } = useTranslation();
  const { keyFacts } = useContext(GiveawayContext);

  if (!keyFacts) {
    return <></>;
  }

  return (
    <StyledContainer>
      <StyledKeyFactsContainer data-testid="keyfacts-container">
        {keyFactsDataGrid(t, keyFacts)
          .filter((kf) => kf.visible)
          .map((keyFact) => (
            <StyledKeyFact key={keyFact.id}>
              {keyFact.icon}
              <StyledDiv>
                <p>{keyFact.title}</p>
                <span
                  data-testid={`keyfact-data-${keyFact.id}`}
                  dangerouslySetInnerHTML={{ __html: keyFact.data || '' }}
                />
              </StyledDiv>
            </StyledKeyFact>
          ))}
      </StyledKeyFactsContainer>
    </StyledContainer>
  );
};
